import { contactInfo } from './siteNav';

export const SITE_NAME = 'Bright Study';
export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://brightstudy.in').replace(/\/$/, '');

export const DEFAULT_KEYWORDS = [
  'School ERP',
  'School Management Software',
  'School Management System',
  'School ERP Software',
  'Education ERP',
  'School Mobile App',
  'School LMS',
  'E-Learning Software',
  'Admission Management Software',
  'Fee Management Software',
  'Best School ERP in India',
  SITE_NAME,
].join(', ');

export const homeSeo = {
  title: `School ERP | School Management Software – ${SITE_NAME}`,
  description:
    `${SITE_NAME} is a school ERP and school management software company in India. Run admissions, academics, fees, LMS, mobile apps, and communication on one platform built for growing schools.`,
  keywords: DEFAULT_KEYWORDS,
};

const page = (title, description, extraKeywords = '') => ({
  title,
  description,
  keywords: extraKeywords ? `${extraKeywords}, ${DEFAULT_KEYWORDS}` : DEFAULT_KEYWORDS,
});

export const pageSeo = {
  '/': homeSeo,
  '/home': homeSeo,
  '/register-for-demo': page(
    `Book a School ERP Demo | School Management Software – ${SITE_NAME}`,
    `Register for a ${SITE_NAME} school ERP demo. See school management software for admissions, fees, academics, LMS, and parent-teacher mobile apps.`,
    'School ERP demo, school management software demo'
  ),
  '/pricing': page(
    `School ERP Pricing | Lifetime License & Custom Plans – ${SITE_NAME}`,
    `${SITE_NAME} school ERP pricing for small schools and custom institutions. Lifetime license plans and fully customizable ERP solutions with secure Razorpay checkout.`,
    'School ERP pricing, school management software price, school ERP license'
  ),
  '/pricing/success': page(
    `Payment Successful | ${SITE_NAME}`,
    `Your ${SITE_NAME} school ERP payment was received. We will share onboarding next steps shortly.`,
    'School ERP payment success'
  ),
  '/platforms': page(
    `School ERP Platforms | LMS, Website, AI & Mobile App – ${SITE_NAME}`,
    `Explore ${SITE_NAME} school ERP platforms: school management system, LMS, school website, AI analytics, and mobile apps for parents, teachers, and leadership.`,
    'School ERP platforms, school software suite'
  ),
  '/platforms/erp': page(
    `School ERP Software | School Management System – ${SITE_NAME}`,
    `${SITE_NAME} School ERP software manages admissions, student information, exams, fees, HR, library, and campus operations in one school management system.`,
    'School ERP software, student information system'
  ),
  '/platforms/lms': page(
    `E-Learning Software | School LMS – ${SITE_NAME}`,
    `School LMS and e-learning software from ${SITE_NAME}: live classes, digital content, homework, assessments, and CBSE-aligned practice inside your school ERP.`,
    'School LMS, e-learning software for schools'
  ),
  '/platforms/website': page(
    `School Website Designing | CMS for Schools – ${SITE_NAME}`,
    `Responsive school website designing with CMS, enquiry capture, notices, and ERP-connected admissions — school management software that also powers your public site.`,
    'School website designing, school CMS'
  ),
  '/platforms/ai': page(
    `AI School ERP | AI for School Management – ${SITE_NAME}`,
    `AI for schools from ${SITE_NAME}: early risk signals, planning support, and faster answers for teachers, parents, and leaders inside school ERP software.`,
    'AI school ERP, AI school management software'
  ),
  '/platforms/ai-analytics-dashboard': page(
    `School Analytics Dashboard | School MIS Software – ${SITE_NAME}`,
    `Live school MIS and AI analytics dashboard for admissions, attendance, academics, and fee collections — school ERP reporting principals can trust.`,
    'School MIS, school analytics dashboard'
  ),
  '/platforms/mobile-application': page(
    `School Mobile App | Parent Teacher App – ${SITE_NAME}`,
    `${SITE_NAME} school mobile app for parents, teachers, and management: homework, fees, attendance, circulars, and transport updates from your school ERP.`,
    'School mobile app, parent teacher app'
  ),
  '/school-optimisation': page(
    `School Management Software | Automate School Operations – ${SITE_NAME}`,
    `Optimise school administration, finance, academics, HR, and communication with ${SITE_NAME} school management software and paperless school ERP workflows.`,
    'School operations software, paperless school ERP'
  ),
  '/school-optimisation/administration': page(
    `School Administration Software | Admission Management – ${SITE_NAME}`,
    `School administration software for enquiry-to-admission, attendance, certificates, and student records — a core module of ${SITE_NAME} School ERP.`,
    'Admission management software, school administration software'
  ),
  '/school-optimisation/finance': page(
    `School Fee Management Software | School Finance ERP – ${SITE_NAME}`,
    `School fee management software with online payment, receipts, concessions, and collection reports. Finance module of ${SITE_NAME} school ERP.`,
    'School fee management software, school finance ERP'
  ),
  '/school-optimisation/learning': page(
    `School Learning Software | Homework & LMS – ${SITE_NAME}`,
    `School learning software for homework, digital content, assessments, and parent visibility — connected to ${SITE_NAME} school management system.`,
    'School homework software, school learning management'
  ),
  '/school-optimisation/academics': page(
    `School Academics Software | Timetable & Exam Management – ${SITE_NAME}`,
    `School academics software for timetables, exams, report cards, and syllabus tracking inside ${SITE_NAME} School ERP and school management software.`,
    'Timetable software for schools, exam management software'
  ),
  '/school-optimisation/intelligence': page(
    `School MIS Software | Principal Dashboard – ${SITE_NAME}`,
    `School MIS software and principal dashboard for admissions, attendance, academics, and fees. Decision-ready school ERP analytics from ${SITE_NAME}.`,
    'School MIS software, principal dashboard'
  ),
  '/school-optimisation/logistics': page(
    `School Transport Software | GPS & Visitor Management – ${SITE_NAME}`,
    `School transport software, visitor management, and campus safety tools connected to student records in ${SITE_NAME} school management software.`,
    'School transport software, school visitor management'
  ),
  '/school-optimisation/leadership-management': page(
    `School Management Software for Principals – ${SITE_NAME}`,
    `School management software for principals and group heads: approvals, campus snapshots, and mobile access to the same school ERP data the office uses.`,
    'School management software for principals'
  ),
  '/school-optimisation/enterprise-features': page(
    `Multi Campus School ERP | Group School Software – ${SITE_NAME}`,
    `Multi-campus school ERP for school groups and franchises. Shared masters, central MIS, and consistent school management software across every campus.`,
    'Multi campus school ERP, group school software'
  ),
  '/school-optimisation/communication': page(
    `School Communication Software | WhatsApp & SMS – ${SITE_NAME}`,
    `School communication software for circulars, remarks, WhatsApp, and SMS — sent once from ${SITE_NAME} School ERP to the channels families already use.`,
    'School WhatsApp integration, school SMS software'
  ),
  '/school-optimisation/human-resources': page(
    `School HR Software | Staff Payroll & Attendance – ${SITE_NAME}`,
    `School HR software for staff records, attendance, leave, and payroll inputs. Human resources module of ${SITE_NAME} school management system.`,
    'School HR software, school payroll software'
  ),
  '/school-optimisation/explore-automation': page(
    `School Automation Software | Paperless School ERP – ${SITE_NAME}`,
    `School automation software that runs admissions, fees, attendance, and communication every day. Paperless school ERP workflows from ${SITE_NAME}.`,
    'School automation software, paperless school ERP'
  ),
  '/success-stories': page(
    `School ERP Success Stories | School Management Software – ${SITE_NAME}`,
    `See how schools use ${SITE_NAME} School ERP and school management software to cut admin work, improve collections, and give leadership a live campus view.`,
    'School ERP case studies'
  ),
  '/success-stories/case-studies': page(
    `School Management Software Case Studies – ${SITE_NAME}`,
    `School ERP case studies from campuses using ${SITE_NAME} school management software for admissions, academics, fees, and parent communication.`,
    'School ERP case studies, school software success stories'
  ),
  '/insights': page(
    `School ERP Insights | Education Technology Blog – ${SITE_NAME}`,
    `School ERP insights, education technology articles, and school management software guides from ${SITE_NAME} for school leaders and educators.`,
    'School ERP blog, education technology'
  ),
  '/insights/coverage': page(
    `School ERP News & Coverage – ${SITE_NAME}`,
    `News and coverage about ${SITE_NAME} school ERP, school management software, and education technology for Indian schools.`,
    'School ERP news'
  ),
  '/insights/video': page(
    `School ERP Videos | School Management Software Demos – ${SITE_NAME}`,
    `Watch ${SITE_NAME} school ERP and school management software videos covering modules, mobile apps, and campus operations.`,
    'School ERP video, school software demo video'
  ),
  '/insights/ed-talks': page(
    `Ed Talks | School ERP & Education Leadership – ${SITE_NAME}`,
    `Ed Talks on school operations, school ERP, and classroom practice from ${SITE_NAME} and education leaders.`,
    'Education talks, school leadership'
  ),
  '/insights/blogs': page(
    `School ERP Blog | School Management Software Articles – ${SITE_NAME}`,
    `Read the ${SITE_NAME} school ERP blog for practical articles on school management software, fees, admissions, and parent communication.`,
    'School ERP blog, school management articles'
  ),
  '/insights/newsletter': page(
    `School ERP Newsletter – ${SITE_NAME}`,
    `Subscribe to the ${SITE_NAME} newsletter for school ERP updates, school management software tips, and product news.`,
    'School ERP newsletter'
  ),
  '/insights/student-zone': page(
    `Student Zone | School ERP Learning Resources – ${SITE_NAME}`,
    `Student zone resources connected to ${SITE_NAME} school LMS and school management software for practice, homework, and progress.`,
    'Student learning portal'
  ),
  '/insights/educator-zone': page(
    `Educator Zone | School ERP for Teachers – ${SITE_NAME}`,
    `Educator zone for teachers using ${SITE_NAME} school ERP: attendance, homework, remarks, and classroom tools in one school management system.`,
    'School ERP for teachers'
  ),
  '/insights/educators-article': page(
    `Educator Articles | School Management Software – ${SITE_NAME}`,
    `Articles for educators on school ERP, classroom operations, and school management software from ${SITE_NAME}.`,
    'Educator articles, school software for teachers'
  ),
  '/about-us': page(
    `About ${SITE_NAME} | School ERP Company in India`,
    `About ${SITE_NAME}, a school ERP and school management software company in India helping schools run admissions, academics, fees, and communication.`,
    'School ERP company in India, school management software company'
  ),
  '/about-us/company': page(
    `${SITE_NAME} Company | School Management Software Developer`,
    `${SITE_NAME} builds school ERP software, school mobile apps, and digital platforms that help schools streamline operations and improve communication.`,
    'School management software company'
  ),
  '/about-us/leadership': page(
    `${SITE_NAME} Leadership | School ERP Team`,
    `Meet the ${SITE_NAME} leadership team behind our school ERP and school management software for growing campuses.`,
    'School ERP company leadership'
  ),
  '/about-us/partners': page(
    `${SITE_NAME} Partners | School ERP Integrations`,
    `Payment, cloud, and communication partners that sit beside ${SITE_NAME} school ERP and school management software.`,
    'School ERP integrations'
  ),
  '/about-us/awards': page(
    `${SITE_NAME} Awards | School Management Software Recognition`,
    `Awards and recognition for ${SITE_NAME} school ERP and school management software used by schools in India.`,
    'School ERP awards'
  ),
  '/contact-us': page(
    `Contact ${SITE_NAME} | School ERP Sales & Support`,
    `Contact ${SITE_NAME} for school ERP software, school management software demos, careers, and support. Talk to sales in New Delhi.`,
    'Contact school ERP company, school software sales'
  ),
  '/contact-us/connect-with-us': page(
    `Connect With ${SITE_NAME} | School ERP Enquiry`,
    `Connect with ${SITE_NAME} for a school ERP walkthrough, pricing conversation, or school management software questions.`,
    'School ERP enquiry, school software contact'
  ),
  '/contact-us/apply-as-a-speaker': page(
    `Apply as a Speaker | ${SITE_NAME} Ed Talks`,
    `Apply as a speaker for ${SITE_NAME} conversations on school ERP, operations, AI, and classroom practice.`,
    'Education speaker application'
  ),
  '/contact-us/career': page(
    `Careers at ${SITE_NAME} | School ERP Jobs`,
    `Careers at ${SITE_NAME}: build school ERP software and school management tools used by campuses every day.`,
    'School ERP jobs, education technology careers'
  ),
  '/privacy-policy': page(
    `Privacy Policy | ${SITE_NAME} Parent App`,
    `Privacy policy for the ${SITE_NAME} Parent app, parent login, and website. How we collect, use, and share parent, student, and enquiry data.`,
    'Privacy policy, Brightstudy Parent app privacy'
  ),
};

export function getPageSeo(path, fallback = {}) {
  const key = path === '/home' ? '/' : path;
  const found = pageSeo[key];
  if (found) return found;
  const label = fallback.title || 'School ERP';
  return page(
    `${label} | School ERP Software – ${SITE_NAME}`,
    fallback.description ||
      `${label} from ${SITE_NAME} school ERP and school management software for admissions, academics, fees, and communication.`,
    label
  );
}

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  email: contactInfo.email,
  telephone: contactInfo.phone,
  address: {
    '@type': 'PostalAddress',
    streetAddress: contactInfo.address,
    addressLocality: 'New Delhi',
    addressCountry: 'IN',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: contactInfo.phone,
    contactType: 'sales',
    email: contactInfo.salesEmail,
    areaServed: 'IN',
    availableLanguage: ['en', 'hi'],
  },
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: homeSeo.description,
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
  },
  inLanguage: 'en-IN',
};

export const softwareSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: `${SITE_NAME} School ERP`,
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'School Management Software',
  operatingSystem: 'Web, Android, iOS',
  url: SITE_URL,
  description: homeSeo.description,
  offers: {
    '@type': 'Offer',
    availability: 'https://schema.org/OnlineOnly',
    url: `${SITE_URL}/register-for-demo`,
  },
  provider: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
  },
  featureList: [
    'School ERP',
    'Admission management',
    'Fee management',
    'School LMS',
    'School mobile app',
    'Academics and exams',
    'Parent communication',
  ],
};

export function breadcrumbSchema(path, pageTitle) {
  const parts = path.split('/').filter(Boolean);
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${SITE_URL}/`,
    },
  ];

  parts.forEach((part, index) => {
    const crumbPath = `/${parts.slice(0, index + 1).join('/')}`;
    const seo = pageSeo[crumbPath];
    items.push({
      '@type': 'ListItem',
      position: index + 2,
      name: index === parts.length - 1 ? pageTitle : seo?.title?.split('|')[0].trim() || part,
      item: `${SITE_URL}${crumbPath}`,
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}
